import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { formatCurrency, formatDate, getStatusColor } from '../../lib/utils';
import {
  Users,
  Truck,
  Calendar,
  DollarSign,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Plus,
  FileText,
  Loader2,
  Group,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentPeriods, setRecentPeriods] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, periodsRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/stats`),
        axios.get(`${API_URL}/payroll-periods`),
      ]);
      setStats(statsRes.data);
      setRecentPeriods(periodsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const CHART_COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))',
  ];

  const pieData = stats ? [
    { name: 'Net Pay', value: stats.gross_vs_net.net },
    { name: 'SSB', value: stats.gross_vs_net.ssb },
    { name: 'Other Ded.', value: stats.gross_vs_net.other },
  ].filter(d => d.value > 0) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header
        title="Dashboard"
        subtitle="Overview of your payroll operations"
        actions={
          <Link to="/payroll/new">
            <Button className="gap-2" data-testid="new-payroll-btn">
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">New Payroll</span>
            </Button>
          </Link>
        }
      />

      <div className="flex-1 p-4 lg:p-8 space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-grid">
          <Card className="card-base animate-slide-up" style={{ animationDelay: '0ms' }}>
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Workers</p>
                  <p className="text-2xl lg:text-3xl font-bold font-heading mt-1">{stats?.total_workers || 0}</p>
                </div>
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Users className="w-5 h-5 lg:w-6 lg:h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-base animate-slide-up" style={{ animationDelay: '50ms' }}>
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Trucks</p>
                  <p className="text-2xl lg:text-3xl font-bold font-heading mt-1">{stats?.total_truckers || 0}</p>
                </div>
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
                  <Truck className="w-5 h-5 lg:w-6 lg:h-6 text-secondary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-base animate-slide-up" style={{ animationDelay: '100ms' }}>
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Payroll Periods</p>
                  <p className="text-2xl lg:text-3xl font-bold font-heading mt-1">{stats?.total_payroll_periods || 0}</p>
                </div>
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                  <Calendar className="w-5 h-5 lg:w-6 lg:h-6 text-accent" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-base animate-slide-up" style={{ animationDelay: '150ms' }}>
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Current Status</p>
                  <p className="text-lg lg:text-xl font-bold font-heading mt-1 capitalize">
                    {stats?.current_period_status || 'No Period'}
                  </p>
                </div>
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-muted flex items-center justify-center">
                  <FileText className="w-5 h-5 lg:w-6 lg:h-6 text-muted-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Weekly Totals Chart */}
          <Card className="card-base animate-slide-up" style={{ animationDelay: '200ms' }}>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Weekly Payroll Totals
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                {stats?.weekly_totals?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.weekly_totals}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis 
                        dataKey="name" 
                        tick={{ fontSize: 12 }}
                        className="text-muted-foreground"
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => `$${value}`}
                        className="text-muted-foreground"
                      />
                      <Tooltip
                        formatter={(value) => formatCurrency(value)}
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '0.5rem',
                        }}
                      />
                      <Bar dataKey="gross" name="Gross Pay" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="net" name="Net Pay" fill={CHART_COLORS[2]} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    No payroll data yet. Create your first payroll period.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Deductions Breakdown */}
          <Card className="card-base animate-slide-up" style={{ animationDelay: '250ms' }}>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-secondary" />
                Current Period Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => formatCurrency(value)}
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '0.5rem',
                        }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    No data for current period.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Payroll Periods */}
        <Card className="card-base animate-slide-up" style={{ animationDelay: '300ms' }}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading flex items-center gap-2">
              <Calendar className="w-5 h-5 text-accent" />
              Recent Payroll Periods
            </CardTitle>
            <Link to="/payroll">
              <Button variant="ghost" size="sm" className="gap-1" data-testid="view-all-periods">
                View all <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentPeriods.length > 0 ? (
              <div className="space-y-3">
                {recentPeriods.map((period) => (
                  <Link
                    key={period.id}
                    to={`/payroll/${period.id}`}
                    className="flex items-center justify-between p-4 rounded-lg border border-border/50 hover:bg-muted/50 transition-colors"
                    data-testid={`period-${period.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{period.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(period.start_date)} - {formatDate(period.end_date)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right hidden sm:block">
                        <p className="font-semibold">{formatCurrency(period.total_net)}</p>
                        <p className="text-sm text-muted-foreground">Net Pay</p>
                      </div>
                      <span className={`status-badge ${getStatusColor(period.status)}`}>
                        {period.status}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No payroll periods yet.</p>
                <Link to="/payroll/new">
                  <Button variant="link" className="mt-2">
                    Create your first payroll period
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/workers" data-testid="quick-action-workers">
            <Card className="card-base hover:shadow-md transition-shadow cursor-pointer h-full">
              <CardContent className="p-4 lg:p-6 flex flex-col items-center text-center">
                <Users className="w-8 h-8 text-primary mb-2" />
                <p className="font-medium">Manage Workers</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/truckers" data-testid="quick-action-truckers">
            <Card className="card-base hover:shadow-md transition-shadow cursor-pointer h-full">
              <CardContent className="p-4 lg:p-6 flex flex-col items-center text-center">
                <Truck className="w-8 h-8 text-secondary mb-2" />
                <p className="font-medium">Manage Trucks</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/owners" data-testid="quick-action-rates">
            <Card className="card-base hover:shadow-md transition-shadow cursor-pointer h-full">
              <CardContent className="p-4 lg:p-6 flex flex-col items-center text-center">
                <Group className="w-8 h-8 text-accent mb-2" />
                <p className="font-medium">Manage Owners</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/reports" data-testid="quick-action-reports">
            <Card className="card-base hover:shadow-md transition-shadow cursor-pointer h-full">
              <CardContent className="p-4 lg:p-6 flex flex-col items-center text-center">
                <TrendingUp className="w-8 h-8 text-muted-foreground mb-2" />
                <p className="font-medium">View Reports</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </div>
  );
}
